import React from "react";
import Headline from "./Headline";
import { Alert, Button, Col, DatePicker, Flex, Form, FormInstance, Input, Modal, notification, Radio, RadioChangeEvent, Row, Select, Space, Spin } from "antd";
import HomeLink from "../../widgets/HomeLink";
import { useNavigate, useSearchParams } from "react-router-dom";
import ListApi from "../../api-services/list-api.service";
import CustomerApi from "../../api-services/customer-api.service";
import B2C2BApi from "../../api-services/b2c2b-api.service";
import { mdiCheckCircleOutline, mdiEmailOutline, mdiPhoneInTalkOutline, mdiWhatsapp } from "@mdi/js";
import Icon from "@mdi/react";
import ChangePasswordForm from "./ChangePasswordForm";
import DOBField from "../../widgets/DOBField";
import { b2c2bDateFormat, bookingFormValidationMessages } from "../../utils/antd.props";
import { useAuth } from "../../services/AuthContext";
import { emailLink, formatDate, isMalaysian, whatsappLink } from "../../utils/helpers";

const bannerPath = '/iceholidays-assets/images/partner_signin_cover.png';
const breadcrumbs = [
  { title: <HomeLink/>},
  { title: 'Profile' }
]

const firstValue = (value) => Array.isArray(value) ? value[0] : value;

function withParams(Component) {
  return props => <Component {...props} navigate={useNavigate()} searchParams={useSearchParams()} auth={useAuth()}/>;
}

class ProfilePage extends React.Component <{navigate, searchParams, auth}>  {
  listApi = new ListApi;
  accountDetailsForm = React.createRef<FormInstance>();
  linkForm = React.createRef<FormInstance>();
  changeAgentForm = React.createRef<FormInstance>();

  state = {
    loading: true,
    user: {},
    showForgotPasswordModal: false,
    showChangeAgentModal: false,
    activeTab: 'default',
    titles: [],
    agentChangeReasons: [],
    assignedAgentLoading: true,
    assignedAgentBinding: null,
    assignedAgent: null,
    pendingAgentChangeRequest: null,
    stickyRequestSubmitting: false,
    stickyRequestCancelling: false,
    showSetupPassword: false,
    countries: [],
    malaysian: true
  }

  componentDidMount() {
    const [searchParams] = this.props.searchParams; 

    Promise.all([
      CustomerApi.getProfile(),
      this.listApi.getTitles(),
      this.listApi.getCountries()
    ]).then(([user, titles, countries]) => {
        return this.listApi.getChangeAgentReasons(user.change_reasons || {})
          .then(agentChangeReasons => ({ user, titles, countries, agentChangeReasons }));
    }).then(({user, titles, countries, agentChangeReasons}) => {
        const setup = searchParams.get("setup");
        if(Boolean(setup)){
          this.setState({activeTab: "security", showSetupPassword: true})
        }

        this.setState({user, titles, loading: false, countries, agentChangeReasons, malaysian: isMalaysian(user.nationality)}, this.loadAssignedAgentSection)
      })
      .catch(error => {
        notification.error({ message: 'Something went wrong.'});
      });
  }

  submitForm(formValues) {
    const {updateUsername} = this.props.auth;
    CustomerApi.updateProfile(formValues)
      .then(user => {
        updateUsername(user.name);
        notification.success({message: "Profile updated successfully."})
      })
  }

  changeTab = (e: RadioChangeEvent) => {
    this.setState({activeTab: e.target.value});
  }

  normalizeBinding(response) {
    const data = response?.data?.data || response?.data || response;
    const attrs = data?.attributes || data;
    if (!attrs) return null;

    const rawAgent = attrs.bound_agent || attrs.agent || attrs.referral_agent;
    const agentAttrs = rawAgent?.attributes || rawAgent;
    const rawAgency = attrs.bound_agency || attrs.agency;
    const agencyAttrs = rawAgency?.attributes || rawAgency;
    const boundAgency = rawAgency ? {
      id: rawAgency.id || agencyAttrs?.id,
      code: agencyAttrs?.code,
      name: agencyAttrs?.full_name || agencyAttrs?.name,
      full_name: agencyAttrs?.full_name,
      email: firstValue(agencyAttrs?.emails) || agencyAttrs?.email,
      emails: agencyAttrs?.emails,
      phone: firstValue(agencyAttrs?.phones) || agencyAttrs?.phone,
      phones: agencyAttrs?.phones,
      address: agencyAttrs?.address,
      city: agencyAttrs?.city,
      state: agencyAttrs?.state
    } : null;
    const rawSubagent = attrs.bound_subagent || attrs.subagent;
    const subagentAttrs = rawSubagent?.attributes || rawSubagent;
    const boundSubagent = rawSubagent ? {
      id: rawSubagent.id || subagentAttrs?.id,
      agency_id: subagentAttrs?.agency_id,
      username: subagentAttrs?.username,
      name: subagentAttrs?.full_name || subagentAttrs?.name,
      full_name: subagentAttrs?.full_name,
      email: firstValue(subagentAttrs?.emails) || subagentAttrs?.email,
      emails: subagentAttrs?.emails,
      phone: firstValue(subagentAttrs?.phones) || subagentAttrs?.phone,
      phones: subagentAttrs?.phones,
      whatsapp: subagentAttrs?.whatsapp_no || subagentAttrs?.whatsapp,
      whatsapp_no: subagentAttrs?.whatsapp_no
    } : null;
    const boundAgent = rawAgent ? {
      id: rawAgent.id || agentAttrs?.id,
      agency_id: agentAttrs?.agency_id,
      username: agentAttrs?.username,
      name: agentAttrs?.full_name || agentAttrs?.name,
      email: firstValue(agentAttrs?.emails) || agentAttrs?.email,
      emails: agentAttrs?.emails,
      image: agentAttrs?.image,
      phone: firstValue(agentAttrs?.phones) || agentAttrs?.phone,
      phones: agentAttrs?.phones,
      whatsapp: agentAttrs?.whatsapp_no || agentAttrs?.whatsapp,
      whatsapp_no: agentAttrs?.whatsapp_no,
      address: agentAttrs?.address,
      agency_full_name: agentAttrs?.agency_full_name
    } : null;

    return {
      id: data?.id || attrs.id,
      source: attrs.source,
      boundAt: attrs.bound_at,
      referralEntityName: attrs.referral_entity_name || attrs.agent_name || attrs.agency_full_name,
      referralEntityType: attrs.referral_entity_type || attrs.agent_type,
      boundAgency,
      boundSubagent,
      boundAgent
    };
  }

  normalizeTracker(tracker) {
    const attrs = tracker?.attributes || tracker;
    if (!attrs) return null;

    return {
      id: tracker?.id || attrs.id,
      status: attrs.status,
      expiresAt: attrs.expires_at,
      changeReason: attrs.change_reason,
      changeReasonText: attrs.change_reason_text,
      otherReason: attrs.other_reason,
      createdAt: attrs.created_at
    };
  }

  assignedAgentName(agent, binding) {
    return binding?.boundAgency?.full_name || binding?.boundAgency?.name || agent?.agency_full_name || agent?.name || agent?.full_name || agent?.username || binding?.referralEntityName || "Assigned agent";
  }

  normalizeTrackersResponse(response) {
    const trackers = response?.data?.data || response?.data || response || [];
    return Array.isArray(trackers) ? trackers : [trackers];
  }

  profileReferralBinding(user) {
    return user?.active_referral_binding || user?.activeReferralBinding || null;
  }

  changeReasonOptions() {
    const reasons:any = this.state.agentChangeReasons || [];

    if (Array.isArray(reasons)) {
      return reasons.map(reason => {
        if (typeof reason === "string") return {value: reason, label: reason};
        return reason;
      });
    }

    return Object.entries(reasons).map(([value, label]) => ({value, label}));
  }

  loadAssignedAgentSection = () => {
    const user:any = this.state.user;
    const profileBinding = this.profileReferralBinding(user);

    if (!user?.email && !profileBinding) {
      this.setState({assignedAgentLoading: false});
      return;
    }

    this.setState({assignedAgentLoading: true});
    const bindingRequest = profileBinding
      ? Promise.resolve(profileBinding)
      : B2C2BApi.getCustomerReferralBinding(user.email);

    Promise.allSettled([
      bindingRequest,
      B2C2BApi.getAgentChangeTrackers()
    ]).then(([bindingResult, trackerResult]) => {
      const binding = bindingResult.status === "fulfilled" ? this.normalizeBinding(bindingResult.value) : null;
      const trackerData = trackerResult.status === "fulfilled" ? this.normalizeTrackersResponse(trackerResult.value) : [];
      const pendingRequest = trackerData
        .map(tracker => this.normalizeTracker(tracker))
        .find(tracker => tracker?.status === "pending") || null;

      this.setState({
        assignedAgentLoading: false,
        assignedAgentBinding: binding,
        assignedAgent: binding?.boundAgent || null,
        pendingAgentChangeRequest: pendingRequest
      });
    });
  }

  submitStickyAgentChangeRequest = (formValues) => {
    if (this.state.pendingAgentChangeRequest) {
      this.changeAgentForm.current?.resetFields();
      this.setState({showChangeAgentModal: false});
      Modal.info({
        title: "Pending change agent request",
        content: "Your current binding remains active during the cooldown period."
      });
      this.loadAssignedAgentSection();
      return;
    }

    this.setState({stickyRequestSubmitting: true});

    B2C2BApi.createStickyAgentChangeRequest({
      change_reason: formValues.reason,
      other_reason: formValues.reason_other
    }).then((response:any) => {
      this.changeAgentForm.current?.resetFields();
      this.setState({showChangeAgentModal: false, stickyRequestSubmitting: false});
      notification.success({message: response?.message || "Release request submitted successfully."});
      this.loadAssignedAgentSection();
    }).catch(err => {
      const errors = err.response?.data?.errors;
      const errorMessage = Array.isArray(errors)
        ? errors.join(" ")
        : err.response?.data?.error || "Unable to submit your request. Please try again.";
      if (err.response?.data?.error_code === "pending_request_exists") {
        Modal.info({
          title: "Pending change agent request",
          content: errorMessage
        });
        this.setState({stickyRequestSubmitting: false});
        this.loadAssignedAgentSection();
        return;
      }

      notification.error({message: errorMessage});
      this.setState({stickyRequestSubmitting: false});
    });
  }

  cancelStickyAgentChangeRequest = () => {
    const pendingRequest:any = this.state.pendingAgentChangeRequest;
    if (!pendingRequest?.id) return;

    this.setState({stickyRequestCancelling: true});

    B2C2BApi.cancelStickyAgentChangeRequest(pendingRequest.id)
      .then(() => {
        notification.success({message: "Release request cancelled successfully."});
        this.setState({stickyRequestCancelling: false});
        this.loadAssignedAgentSection();
      })
      .catch(err => {
        const errorMessage = err.response?.data?.error || "Unable to cancel your request. Please try again.";
        notification.error({message: errorMessage});
        this.setState({stickyRequestCancelling: false});
      });
  }

  renderAssignedAgentSection() {
    const {
      assignedAgentBinding,
      assignedAgent,
      assignedAgentLoading,
      pendingAgentChangeRequest,
      stickyRequestSubmitting,
      stickyRequestCancelling
    }:any = this.state;

    const hasBinding = !!assignedAgentBinding;
    const agentName = hasBinding ? this.assignedAgentName(assignedAgent, assignedAgentBinding) : "";
    const subagentName = assignedAgentBinding?.boundSubagent?.full_name || assignedAgentBinding?.boundSubagent?.name || assignedAgentBinding?.boundSubagent?.username;
    const agentEmail = assignedAgent?.email || firstValue(assignedAgent?.emails);
    const agentWhatsapp = assignedAgent?.whatsapp_no || assignedAgent?.whatsapp;
    const boundAt = assignedAgentBinding?.boundAt ? formatDate(assignedAgentBinding.boundAt, "DD MMM YYYY HH:mm") : null;
    const sourceText = assignedAgentBinding?.source === "referral" ? "Acquired via referral" : "Self-selected";
    const pendingExpiresAt = pendingAgentChangeRequest?.expiresAt
      ? formatDate(pendingAgentChangeRequest.expiresAt, "DD MMM YYYY HH:mm")
      : null;

    const reasonOptions = this.changeReasonOptions();

    return (
      <div className="assigned-agent-section">
        <Flex vertical gap={16}>
          <div className="assigned-agent-section-header">
            <h3>Your assigned agent</h3>
          </div>

          {assignedAgentLoading ? (
            <Spin />
          ) : !hasBinding ? (
            <Alert
              type="info"
              showIcon
              message="You currently have no assigned agent."
              description="Your next booking will set one."
            />
          ) : (
            <div className="assigned-agent-card">
              <Flex justify="space-between" align="flex-start" gap={20} wrap>
                <div className="assigned-agent-details">
                  <h4>{agentName}</h4>
                  {subagentName && subagentName !== agentName && (
                    <div className="assigned-agent-meta">
                      <span>Travel Advisor: {subagentName}</span>
                    </div>
                  )}
                  <Space size={8} wrap>
                    {agentWhatsapp && (
                      <Button href={whatsappLink(agentWhatsapp)} target="_blank" icon={<Icon path={mdiWhatsapp} size="14px" />} color="primary" variant="outlined">
                        {agentWhatsapp}
                      </Button>
                    )}
                    {agentEmail && (
                      <Button href={emailLink(agentEmail)} target="_blank" icon={<Icon path={mdiEmailOutline} size="14px" />} color="primary" variant="text">
                        Email
                      </Button>
                    )}
                    {assignedAgent?.phone && (
                      <Button href={`tel:${assignedAgent.phone}`} icon={<Icon path={mdiPhoneInTalkOutline} size="14px" />} color="primary" variant="text">
                        {assignedAgent.phone}
                      </Button>
                    )}
                  </Space>
                  <div className="assigned-agent-meta">
                    {boundAt && <span>Bound at (Kuala Lumpur): {boundAt}</span>}
                    <span>{sourceText}</span>
                  </div>
                  <p>
                    We will update your preferred agency only if you request a release.
                  </p>
                </div>

                {!pendingAgentChangeRequest && (
                  <Button type="primary" onClick={() => this.setState({showChangeAgentModal: true})}>
                    Request to release my agent assignment
                  </Button>
                )}
              </Flex>

              {pendingAgentChangeRequest && (
                <Alert
                  type="warning"
                  showIcon
                  message={`Pending release request${pendingExpiresAt ? ` - your assignment will be released at ${pendingExpiresAt} unless cancelled` : ""}`}
                  description={
                    <Flex vertical gap={12}>
                      <span>
                        Your current binding remains active during the cooldown period.
                      </span>
                      <Button
                        danger
                        loading={stickyRequestCancelling}
                        onClick={this.cancelStickyAgentChangeRequest}
                      >
                        Cancel request
                      </Button>
                    </Flex>
                  }
                  style={{marginTop: 16}}
                />
              )}
            </div>
          )}
        </Flex>

        <Modal title="Request to release my agent assignment" open={this.state.showChangeAgentModal} onCancel={() => this.setState({showChangeAgentModal:false})} footer={null} width={800} centered className="change_agent_modal" destroyOnClose>
          <Form
            ref={this.changeAgentForm}
            autoComplete="off"
            layout="vertical"
            variant="filled"
            validateMessages={bookingFormValidationMessages}
            onFinish={this.submitStickyAgentChangeRequest}
            className="change_agent_modal-form"
          >
            <p>
              Your current agent assignment will remain with {agentName} during the cooldown period. After the cooldown period, you'll be released from this assignment and free to book with a different agent on your next booking.
            </p>

            <Form.Item label="Reason for Changing Agent" name="reason" rules={[{required: true}]}>
              <Select placeholder="Select" options={reasonOptions}/>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(previous, current) => previous.reason !== current.reason}>
              {({getFieldValue}) => ["Others", "others"].includes(getFieldValue("reason")) && (
                <Form.Item label="Please Specify your Reason" name="reason_other" rules={[{required: true}]}>
                  <Input placeholder="Reason"/>
                </Form.Item>
              )}
            </Form.Item>

            <Flex align="center" justify="center">
              <Button type="primary" htmlType="submit" loading={stickyRequestSubmitting}>
                Submit request
              </Button>
            </Flex>
          </Form>
        </Modal>
      </div>
    )
  }

  renderContent() {
    const {titles, user, countries, malaysian} = this.state;
    const {logout} = this.props.auth;

    const changePasswordResponseHandler = (success:boolean, title:string, message:string, error?) => {
      if(success){
        Modal.success({
          icon: null,
          title: <h3> <Icon path={mdiCheckCircleOutline} size={1} /> {title} </h3>,
          content: <div>
                    {message}
                    <p>All existing accounts have been logged out, please login with your new password again.</p>
                  </div>,
          footer: <Flex align="center" justify="center">
                    <Button type="primary" onClick={logout}>
                      Login
                    </Button>
                  </Flex>,
          className: "reset-password-success-modal",
          width: 500,
          closable: false
        });
      }else{
        notification.error({message: error});
      }
    }

    const submitPassword = (formValues) => {
      const passwordPayload = {
        password: formValues.password,
        password_confirmation: formValues.confirm,
      };

      CustomerApi.setupPassword(passwordPayload)
        .then(response => {
          const title = "Password Setup Successful!";
          const message = "Your password has been set.";
          const success = response.success;
          const errorMessages = response.error;
          changePasswordResponseHandler(success, title, message, errorMessages);
        })
        .catch(err => notification.error({message: err.response.data.error}))
    }

    const sendRequest = (formValues) => {
      CustomerApi.updatePassword(formValues)
        .then(response => {
          const title =  "Password Reset Successful!";
          const message = "Your password has been reset.";
          const success = response.message == 'success';
          changePasswordResponseHandler(success, title, message);
        })
        .catch(err => notification.error({message: err.response.data.error}))
    };

    const ResetPasswordView = (
      <>
        <label>Reset Password</label>
        <p>To reset your password, please enter your username</p>
        <Form
          autoComplete="off"
          layout="vertical"
          variant="filled"
          onFinish={sendRequest}
        >
          <Form.Item label="Current Password" name="current_password" rules={[{required: true, message: "Current Password is required"}]}>
            <Input.Password placeholder="Enter Current Password" />
          </Form.Item>

          <Form.Item label="New Password" name="password" rules={[{required: true, message: "Password is required"}]}>
            <Input.Password placeholder="Enter New Password" />
          </Form.Item>

          <Form.Item label="Confirm Password" name="confirm_password" 
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password!'},
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The new password that you entered do not match!'));
                  },
                }),
              ]}>
            <Input.Password placeholder="Enter Confirm Password" />
          </Form.Item>

          <Flex align="center" justify="center"  className="profile-page-form-action-btns">
            <Button type="primary" htmlType="submit">
              Submit
            </Button>
          </Flex>
        </Form>
      </> 
    );

    const SetupPasswordView =  <ChangePasswordForm submit={submitPassword}/>

    switch(this.state.activeTab) {
      case "security":
        return  this.state.showSetupPassword ? SetupPasswordView : ResetPasswordView
      default:
       return <>
        {this.renderAssignedAgentSection()}
        <label>Details</label>
        <Form
          ref={this.accountDetailsForm}
          autoComplete="off"
          layout="vertical"
          variant="filled"
          scrollToFirstError
          initialValues={user}
          validateMessages={bookingFormValidationMessages}
          onFinish={(formValues)=>this.submitForm(formValues)}
        >
          <Row gutter={[20,20]}>
              <Col xs={24} md={12}>
                <Form.Item label="Designation" name="designation" rules={[{required: true}]}>
                  <Select placeholder="Mr" options={titles}/>
                </Form.Item>
              </Col>
          </Row>          

          <Row gutter={[20,20]}>              
              <Col xs={24} md={12}>
                <Form.Item label="Surname on Passport" name="surname"  rules={[{required: true}]}>
                  <Input placeholder="Enter Surname on Passport" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Given name on Passport" name="name"  rules={[{required: true}]}>
                  <Input placeholder="Enter Given name on Passport" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Mobile Number" name="phone"  rules={[{required: true}]}>
                  <Input placeholder="Enter Mobile Number" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Email" name="email" rules={[{ type: 'email' }, {required: true}]}>
                  <Input placeholder="Enter Email" disabled/>
                </Form.Item>
              </Col>
              <Col sm={24} lg={12}>
                <Form.Item label="Nationality" name="nationality" rules={[{required: true}]}>
                  <Select 
                    options={countries} 
                    placeholder="Nationality"
                    allowClear
                    showSearch 
                    onChange={(selected)=>this.setState({malaysian: isMalaysian(selected)})}/>
                </Form.Item>
              </Col>
              <Col sm={24} lg={12}>
                <Form.Item label="NRIC" name="nric" 
                  rules={[{required: malaysian}, { pattern: /^\d{12}$/ }]}>
                  <Input placeholder="900101010001" maxLength={12}/>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Passport Number" name="passport_number">
                  <Input placeholder="Enter Passport Number" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Passport Expiry" name="passport_expiry_date">
                  <DatePicker picker="date" format={b2c2bDateFormat} inputReadOnly/>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <DOBField/>
              </Col>
          </Row>          

          <Flex align="center" justify="center"  className="profile-page-form-action-btns">
            <Button type="primary" htmlType="submit">
              Update Profile
            </Button>
          </Flex>
        </Form>
       </>
    }
  }


  render(){
    return !this.state.loading && (
      <div id="profile" className="gd-series-tour-page profile-page">
        <Headline showSearch={false} bannerImage={bannerPath} breadcrumbs={breadcrumbs}>
          <span className="title">
            Profile
          </span>
        </Headline>

        <div className="gd-series-tour-page_body">
          <div className="profile-page-form">
            
            <Flex justify="center" className="profile-page-form-header">
              <Radio.Group className="custom-tab-pill" value={this.state.activeTab} onChange={this.changeTab}>
                <Radio.Button value="default">Account Details</Radio.Button>
                <Radio.Button value="security">Security Details</Radio.Button>
              </Radio.Group>
            </Flex>

            {this.renderContent()}
          </div>
        </div>

      </div>
    )
  }
}

export default withParams(ProfilePage);
